import { ResponseEntity } from "domain/entity/Common/ResponseEntity";
import { RevenueReconciliationReportEntity } from "domain/entity/RevenueReconciliationReport/RevenueReconciliationReportEntity";
import { PrintQueueRepository } from "domain/repository/Common/PrintQueue";
import { ExchangeRateRepository } from "domain/repository/ExchangeRate/ExchangeRateRepo";
import { RevenueReconciliationReportRepository } from "domain/repository/RevenueReconciliationReport/RevenueReconciliationReportRepo";
import { UserRepository } from "domain/repository/User/UserRepo";
import _ from "lodash";
import { AllNbisivPermission } from "presentation/constant/ANAInfo/NbisivPermission";
import { collectionSubTypeDropdownOption } from "presentation/constant/DropDownOptions/StaticDropdownOptions";
import { createRevenueReconciliationReportValidationSchema } from "presentation/constant/RevenueReconciliationReport/RevenueReconciliationReportValidationSchema";
import { Validation } from "presentation/constant/Validation";
import { RevenueReconciliationReportModel } from "presentation/model/RevenueReconciliationReport/RevenueReconciliationReportModel";
import { Dispatch, SetStateAction } from "react";
import { GroupCheckboxList, IFieldValue } from "veronica-ui-component/dist/component/core";
import BaseViewModel from "../BaseViewModel";

interface RevenueReconciliationReportVMProps extends BaseViewModel {
    dispatch: [
        Dispatch<SetStateAction<RevenueReconciliationReportModel>> | ((value: SetStateAction<RevenueReconciliationReportModel>) => void),
    ],
    revenueReconciliationReportRepo: RevenueReconciliationReportRepository,
    printQueueRepo: PrintQueueRepository,
    exchangeRateRepo: ExchangeRateRepository,
    userRepo: UserRepository,
}

export const RevenueReconciliationReportVM = ({ dispatch, revenueReconciliationReportRepo, printQueueRepo, exchangeRateRepo, userRepo }: RevenueReconciliationReportVMProps) => {
    const [revenueReconciliationReportDispatch] = dispatch;

    const loadDropdownOption = async () => {

        await exchangeRateRepo.getAllCurrencies().then(
            currencies => {
                let currencyDropdownOptions = currencies?.map((currency) => ({
                    dropdownLabel: currency.currencyCode,
                    tagLabel: currency.currencyCode,
                    value: currency.currencyCode,
                })) ?? []
                currencyDropdownOptions = _.orderBy(currencyDropdownOptions, "dropdownLabel");
                revenueReconciliationReportDispatch(prevState => ({
                    ...prevState,
                    dynamicOptions: {
                        ...prevState.dynamicOptions,
                        currencyDropdownOptions: currencyDropdownOptions,
                    },
                    currentReport: {
                        ...prevState.currentReport,
                        currency: currencyDropdownOptions.length > 0 ? currencyDropdownOptions[0].value : '',
                    }
                }))
            }
        )


        await printQueueRepo.getAllPrintQueues().then(
            datas => {
                let printQueues = datas.filter(entity => entity.activeInd === 'Y');

                const dataObj = printQueues.reduce((acc, item) => {
                    const key = item.id;
                    const value = item.printQueueName;
                    acc[value] = key;
                    return acc;
                }, {} as { [key: string]: number });


                let printQueueDropdownOptions = printQueues?.map((printQueue) => ({
                    dropdownLabel: printQueue.printQueueName,
                    tagLabel: printQueue.printQueueName,
                    value: printQueue.printQueueName,
                })) ?? []
                printQueueDropdownOptions = _.orderBy(printQueueDropdownOptions, "dropdownLabel");

                revenueReconciliationReportDispatch(prevState => ({
                    ...prevState,
                    printQueueIdMap: dataObj,
                    dynamicOptions: {
                        ...prevState.dynamicOptions,
                        printQueueDropdownOptions: printQueueDropdownOptions,
                    },
                    currentReport: {
                        ...prevState.currentReport,
                        printer: printQueueDropdownOptions.length > 0 ? printQueueDropdownOptions[0].value : '',
                    }
                }))
            }
        )

        await userRepo.getAllUsers().then(users => {
            let newUsers = _.orderBy(users, ["name"]);
            const cashierIdDropdownOption = newUsers?.map((user) => ({
                dropdownLabel: user.name,
                tagLabel: user.name,
                value: user.name,
            })) ?? []

            revenueReconciliationReportDispatch(prevState => ({
                ...prevState,
                dynamicOptions: {
                    ...prevState.dynamicOptions,
                    cashierIdDropdownOptions: cashierIdDropdownOption,
                }
            }))
        });

        revenueReconciliationReportDispatch(prevState => ({
            ...prevState,
            dynamicOptions: {
                ...prevState.dynamicOptions,
                subTypeDropdownOptions: collectionSubTypeDropdownOption,
            },
        }))
    }

    const initRevenueReconciliationReportList = async (permissions: AllNbisivPermission) => {
        await revenueReconciliationReportRepo.initRevenueReconciliationReportList().then(data => {
            // const permissionKeys = Object.keys(permissions) ?? [];
            const [reportIds, reportNames] = data.reduce<[string[], string[]]>((acc, item) => {
                const [key, value] = item.split(':', 2);

                //const rptPermission = generatePermissionByRptId(ReportView.REVENUE_RECONCILIATION_REPORT, key)
                //const permission = permissions[rptPermission];

                //if (permission && permission.acl && permission.acl.includes('R')) {// keep as old nbis logic first
                acc[0].push(key);
                acc[1].push(value.trim());
                // }
                return acc;
            }, [[], []] as [string[], string[]]);

            const dataObj = data.reduce((acc, item) => {
                const [key, value] = item.split(':', 2);
                acc[value] = key.trim();
                return acc;
            }, {} as { [key: string]: string });

            let reportTypeCheckboxOption = reportNames.map((reportName) => ({
                key: reportName,
                name: reportName,
                disable: false,
            })) ?? []

            reportTypeCheckboxOption = _.orderBy(reportTypeCheckboxOption, "name");
            revenueReconciliationReportDispatch(prevState => {
                return {
                    ...prevState,
                    reportIdTypeMap: dataObj,
                    reportIdList: reportIds,
                    reportTypeCheckboxOptions: reportTypeCheckboxOption,
                }
            })
        })
    }

    const onHeaderFieldChange = async (fieldKey: string, fieldValue: IFieldValue, fFullValue?: any) => {
        let val: any = fieldValue;

        revenueReconciliationReportDispatch(prevState => {
            return {
                ...prevState,
                currentReport: {
                    ...prevState.currentReport,
                    [fieldKey]: val,
                },
                masterState: {
                    ...prevState.masterState,
                    allFormState: {
                        ...prevState.masterState.allFormState,
                        [fieldKey]: ''
                    },
                }
            }
        })
    }


    const onResetClick = async () => {
        revenueReconciliationReportDispatch(prevState => ({
            ...prevState,
            currentReport: {
                ...prevState.currentReport,
                reportTypeList: [],
                currency: prevState.dynamicOptions.currencyDropdownOptions.length > 0 ? prevState.dynamicOptions.currencyDropdownOptions[0].value : '',
                cutOffDateFrom: null,
                cutOffDateTo: null,
                printer: prevState.dynamicOptions.printQueueDropdownOptions.length > 0 ? prevState.dynamicOptions.printQueueDropdownOptions[0].value : '',
                cutOffDate2ndFrom: null,
                cutOffDate2ndTo: null,
                docDate: null,
                cashierId: null,
                opsDateFrom: null,
                opsDateTo: null,
                subType: null,
            }
        }))
    }

    const onPrintClick = async (reportIdTypeMap: { [key: string]: string }, entity: RevenueReconciliationReportEntity) => {
        const valResult = await Validation(createRevenueReconciliationReportValidationSchema).ValidateFormOnly(entity);
        let validatedResult: { [x: string]: string } = {};
        if (valResult) {
            validatedResult = { ...valResult };

            revenueReconciliationReportDispatch(prevState => {
                return {
                    ...prevState,
                    masterState: {
                        ...prevState.masterState,
                        allFormState: {
                            ...validatedResult
                        },
                    }
                }
            });

            const res: ResponseEntity = {
                code: "",
                success: false,
                msg: null,
                data: null
            }
            return res;
        } else {
            let data = { ...entity };
            data = {
                ...data,
                reportTypeList: data.reportTypeList.map(type =>
                    reportIdTypeMap[type] || type
                )
            }
            return await revenueReconciliationReportRepo.print(data);
        }
    }


    const onViewClick = async (reportIdTypeMap: { [key: string]: string }, entity: RevenueReconciliationReportEntity) => {
        const valResult = await Validation(createRevenueReconciliationReportValidationSchema).ValidateFormOnly(entity);
        let validatedResult: { [x: string]: string } = {};
        if (valResult) {
            validatedResult = { ...valResult };

            revenueReconciliationReportDispatch(prevState => {
                return {
                    ...prevState,
                    masterState: {
                        ...prevState.masterState,
                        allFormState: {
                            ...validatedResult
                        },
                    }
                }
            });

            const res: ResponseEntity = {
                code: "",
                success: false,
                msg: null,
                data: null
            }
            return res;
        } else {
            let data = { ...entity };
            data = {
                ...data,
                reportTypeList: data.reportTypeList.map(type =>
                    reportIdTypeMap[type] || type
                )
            }
            let msg = "";
            await revenueReconciliationReportRepo.view(data).then((data) => {
                if (data && data.success) {
                    const res = data.data;
                    for (const key in res) {
                        if (res[key]) {
                            const tempData = res[key] as string;
                            let base64 = tempData;
                            let bstr = atob(base64);
                            let n = bstr.length;
                            let u8arr = new Uint8Array(n);
                            while (n--) {
                                u8arr[n] = bstr.charCodeAt(n)
                            }
                            const blob = new Blob([u8arr], { type: 'application/pdf' });
                            const url = URL.createObjectURL(blob);
                            window.open(url, '_blank');
                            //URL.revokeObjectURL(url);
                        }
                    }
                } else {
                    msg = data.data ?? "View Failed";
                }
            })
            const res: ResponseEntity = {
                code: "",
                success: msg ? false : true,
                msg: null,
                data: msg
            }
            return res;
        }
    }


    const onGroupCheckboxChange = (e: (GroupCheckboxList | undefined)[]) => {
        let selectedValue: string[] = [];
        if (e) {
            e?.forEach(function (value, index) {
                if (value) {
                    selectedValue.push(value.key);
                }
            });
        }
        revenueReconciliationReportDispatch(prevState => ({
            ...prevState,
            currentReport: {
                ...prevState.currentReport,
                reportTypeList: selectedValue,
            }
        }));
    };

    const onSaveClicked = () => {
        revenueReconciliationReportDispatch(prevState => {
            return {
                ...prevState,
                masterState: {
                    ...prevState.masterState,
                    isSaveClicked: true,
                    allFormState: {},
                }
            }
        })
    }

    return {
        onGroupCheckboxChange: onGroupCheckboxChange,
        onPrintClick: onPrintClick,
        onViewClick: onViewClick,
        onResetClick: onResetClick,
        onHeaderFieldChange: onHeaderFieldChange,
        loadDropdownOption: loadDropdownOption,
        initRevenueReconciliationReportList: initRevenueReconciliationReportList,
        onSaveClicked: onSaveClicked,
    }
}